import { Sorts } from '@/models';
import i18n from '@/plugins/i18n';
import customSortDates from '@/utils/customSortDates';

/**
 * The Shop Brands sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function shopBrandsSortSchema(): Readonly<Sorts> {
  const defaultSort = i18n.t('common.default');
  const asc = i18n.t('ll.sorts.brandNameAsc');
  const desc = i18n.t('ll.sorts.brandNameDesc');
  const sortNew = i18n.t('ll.sorts.dateDesc');
  const lastUpdateDesc = i18n.t('ll.sorts.menuUpdateDesc');
  const lastUpdateAsc = i18n.t('ll.sorts.menuUpdateAsc');
  const count = i18n.t('ll.sorts.productCountDesc');

  return Object.freeze({
    [defaultSort]: '',
    [asc]: {
      contentApiId: 'name',
      sortBy: 'name',
    },
    [desc]: {
      contentApiId: '-name',
      sortBy: 'name',
      sortDesc: true,
    },
    [sortNew]: {
      sortBy: 'createdOn',
      sortDesc: true,
      customSorter: customSortDates,
    },
    [lastUpdateDesc]: {
      contentApiId: '-last_update',
      sortBy: 'lastUpdate',
      sortDesc: true,
      customSorter: customSortDates,
    },
    [lastUpdateAsc]: {
      contentApiId: 'last_update',
      sortBy: 'lastUpdate',
      customSorter: customSortDates,
    },
    [count]: {
      contentApiId: '-product_count',
      sortBy: 'productCount',
      sortDesc: true,
    },
  });
}

/**
 * The deals sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function dealsSortSchema(): Readonly<Sorts> {
  const discountAsc = i18n.t('ll.sorts.discountAsc');
  const discountDesc = i18n.t('ll.sorts.discountDesc');
  const priceAsc = i18n.t('ll.sorts.priceAsc');
  const priceDesc = i18n.t('ll.sorts.priceDesc');
  const productAsc = i18n.t('ll.sorts.productNameAsc');
  const productDesc = i18n.t('ll.sorts.productNameDesc');
  const brandAsc = i18n.t('ll.sorts.brandNameAsc');
  const brandDesc = i18n.t('ll.sorts.brandNameDesc');

  return Object.freeze({
    [discountAsc]: { sortBy: 'discount' },
    [discountDesc]: { sortBy: 'discount', sortDesc: true },
    [priceAsc]: {
      sortBy: 'salePrice',
      customSorter: (a, b) => (a.amount > b.amount ? 1 : -1),
    },
    [priceDesc]: {
      sortBy: 'salePrice',
      customSorter: (a, b) => (a.amount < b.amount ? 1 : -1),
    },
    [productAsc]: { sortBy: 'name' },
    [productDesc]: { sortBy: 'name', sortDesc: true },
    [brandAsc]: { sortBy: 'brand.name' },
    [brandDesc]: { sortBy: 'brand.name', sortDesc: true },
  });
}

/**
 * The brand menu products sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function brandMenuSortSchema(): Readonly<Sorts> {
  const sorts = {
    default: i18n.t('common.default'),
    createdOnAsc: i18n.t('ll.sorts.createdDateAsc'),
    createdOnDesc: i18n.t('ll.sorts.createdDateDesc'),
  };

  return Object.freeze({
    [sorts.default]: '',
    [sorts.createdOnAsc]: { sortBy: 'createdOn' },
    [sorts.createdOnDesc]: { sortBy: 'createdOn', sortDesc: true },
  });
}

/**
 * The vendor menu products sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function vendorMenuSortSchema({ showBrandSort }: { showBrandSort: boolean }): Readonly<Sorts> {
  const defaultSort = i18n.t('common.default');
  const createdOnAsc = i18n.t('ll.sorts.createdDateAsc');
  const createdOnDesc = i18n.t('ll.sorts.createdDateDesc');
  const brandAsc = i18n.t('ll.sorts.brandNameAsc');
  const brandDesc = i18n.t('ll.sorts.brandNameDesc');

  const sorts = {
    [defaultSort]: '',
    ...(showBrandSort
      ? {
          [brandAsc]: { sortBy: 'brand.name' },
          [brandDesc]: { sortBy: 'brand.name', sortDesc: true },
        }
      : {}),
    [createdOnAsc]: { sortBy: 'createdOn' },
    [createdOnDesc]: { sortBy: 'createdOn', sortDesc: true },
  };

  return Object.freeze(sorts);
}

/**
 * The purchase history products sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function purchaseHistorySortSchema(): Readonly<Sorts> {
  const lastOrdered = i18n.t('ll.sorts.lastOrderDateDesc');
  const priceAsc = i18n.t('ll.sorts.priceAsc');
  const priceDesc = i18n.t('ll.sorts.priceDesc');

  return Object.freeze({
    [lastOrdered]: {
      sortBy: '[0].orderDate',
      sortDesc: true,
    },
    [priceAsc]: {
      sortBy: '[0].product.effectivePrice.amount',
    },
    [priceDesc]: {
      sortBy: '[0].product.effectivePrice',
      sortDesc: true,
    },
  });
}

/**
 * The Purchase History sorting schema
 */
export function purchaseHistoryNewSortSchema(): Readonly<Sorts> {
  const brandNameAsc = i18n.t('ll.sorts.brandNameAsc');
  const brandNameDesc = i18n.t('ll.sorts.brandNameDesc');
  const productNameAsc = i18n.t('ll.sorts.productNameAsc');
  const productNameDesc = i18n.t('ll.sorts.productNameDesc');
  const priceAsc = i18n.t('ll.sorts.priceAsc');
  const priceDesc = i18n.t('ll.sorts.priceDesc');
  const lastOrderDateAsc = i18n.t('ll.sorts.lastOrderDateAsc');
  const lastOrderDateDesc = i18n.t('ll.sorts.lastOrderDateDesc');

  return Object.freeze({
    [brandNameAsc]: 'brand',
    [brandNameDesc]: '-brand',
    [productNameAsc]: 'product_name',
    [productNameDesc]: '-product_name',
    [priceAsc]: 'price',
    [priceDesc]: '-price',
    [lastOrderDateAsc]: 'order_date',
    [lastOrderDateDesc]: '-order_date',
  });
}

/**
 * The purchase list products sorting schema
 * @returns {Sorts} The sorting schema.
 */
export function purchaseListSortSchema(): Readonly<Sorts> {
  const priceAsc = i18n.t('ll.sorts.priceAsc');
  const priceDesc = i18n.t('ll.sorts.priceDesc');

  return Object.freeze({
    [priceAsc]: {
      sortBy: 'product.effectivePrice.amount',
    },
    [priceDesc]: {
      sortBy: 'product.effectivePrice.amount',
      sortDesc: true,
    },
  });
}

/**
 * The Store Favorites sorting schema
 */
export function storeFavoritesSortSchema(): Readonly<Sorts> {
  const productNameAsc = i18n.t('ll.sorts.productNameAsc');
  const productNameDesc = i18n.t('ll.sorts.productNameDesc');
  const priceAsc = i18n.t('ll.sorts.priceAsc');
  const priceDesc = i18n.t('ll.sorts.priceDesc');
  const quantityAsc = i18n.t('ll.sorts.quantityAsc');
  const quantityDesc = i18n.t('ll.sorts.quantityDesc');
  const lastOrderDateAsc = i18n.t('ll.sorts.lastOrderDateAsc');
  const lastOrderDateDesc = i18n.t('ll.sorts.lastOrderDateDesc');

  return Object.freeze({
    [productNameAsc]: {
      sortBy: 'product.displayName',
    },
    [productNameDesc]: {
      sortBy: 'product.displayName',
      sortDesc: true,
    },
    [priceAsc]: {
      sortBy: 'product.effectivePrice.amount',
    },
    [priceDesc]: {
      sortBy: 'product.effectivePrice.amount',
      sortDesc: true,
    },
    [lastOrderDateAsc]: {
      sortBy: 'lastOrderDate',
    },
    [lastOrderDateDesc]: {
      sortBy: 'lastOrderDate',
      sortDesc: true,
    },
    [quantityAsc]: {
      sortBy: 'quantity',
    },
    [quantityDesc]: {
      sortBy: 'quantity',
      sortDesc: true,
    },
  });
}

/**
 * The public menu products sorting schema
 */
export function publicMenuSortSchema() {
  const sorts = {
    default: i18n.t('common.default'),
    productNameAsc: i18n.t('ll.sorts.productNameAsc'),
    productNameDesc: i18n.t('ll.sorts.productNameDesc'),
    createdOnAsc: i18n.t('ll.sorts.createdDateAsc'),
    createdOnDesc: i18n.t('ll.sorts.createdDateDesc'),
  };

  return Object.freeze({
    [sorts.default]: '',
    [sorts.productNameAsc]: 'display_name',
    [sorts.productNameDesc]: '-display_name',
    [sorts.createdOnAsc]: 'created_on',
    [sorts.createdOnDesc]: '-created_on',
  });
}
